import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCreditCard, FaCalendarAlt, FaClock, FaUser, FaEnvelope, FaHistory } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { languageOptions, translations, type Language } from '../i18n/translations';
import { formatCurrency, formatDate, getRemainingDays } from '../utils/formatters';
import { getUserSubscription as getBackendSubscription } from '../services/subscription';
import { requestPortalSession } from '../services/backendApi';

// 样式组件
const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const NoSubscriptionMessage = styled.div`
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  margin: 40px 0;
`;

const NoSubscriptionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #333;
`;

const NoSubscriptionText = styled.p`
  font-size: 1rem;
  color: #666;
  margin-bottom: 24px;
`;

const LanguageToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const LanguageLabel = styled.span`
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
  margin-right: 4px;
`;

const LanguageToggleButton = styled.button<{ $active: boolean }>`
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid ${props => (props.$active ? '#4f46e5' : 'rgba(79, 70, 229, 0.3)')};
  background: ${props => (props.$active ? 'rgba(79, 70, 229, 0.12)' : 'white')};
  color: ${props => (props.$active ? '#4f46e5' : '#4a5568')};
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(79, 70, 229, 0.12);
  }
`;

const SubscribeButton = styled.button`
  background-color: #4f46e5;
  color: white;
  font-weight: 600;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #4338ca;
  }
`;

const SubscriptionCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const PlanName = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 10px;
  background-color: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#22c55e';
      case 'canceled':
        return '#f97316';
      case 'past_due':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }};
  color: white;
`;

const PriceInfo = styled.div`
  font-size: 1.4rem;
  font-weight: 600;
  color: #4f46e5;
  margin: 12px 0;
`;

const PeriodInfo = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  margin: 6px 0;
  font-size: 0.9rem;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  margin-right: 8px;
  color: #4f46e5;
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 30px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 30px;
`;

const ActionButton = styled.button<{ $primary?: boolean }>`
  background-color: ${props => props.$primary ? '#4f46e5' : 'white'};
  color: ${props => props.$primary ? 'white' : '#4f46e5'};
  border: 1px solid ${props => props.$primary ? 'transparent' : '#4f46e5'};
  padding: 10px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: ${props => props.$primary ? '#4338ca' : 'rgba(79, 70, 229, 0.05)'};
  }
`;

const UserInfoSection = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 30px;
  margin-bottom: 30px;
`;

const getStatusText = (status: string, language: Language): string => {
  const statusTranslations = translations[language].status as Record<string, string>;
  if (!status) return statusTranslations.unknown;

  return statusTranslations[status] || status;
};

const getPlanName = (planId: string, interval: string, language: Language) => {
  const planNames = translations[language].planNames as Record<string, string>;
  if (!planId) return planNames.unknown;

  if (planId.includes('pro') && interval === 'month') {
    return planNames.monthly;
  } else if (planId.includes('pro') && interval === 'year') {
    return planNames.yearly;
  } else if (planId.includes('pro') && (interval === 'lifetime' || interval === undefined)) {
    return planNames.lifetime;
  }

  return planNames.standard;
};

// 判断订阅是否为终身版本
const isLifetimeSubscription = (subscription: any) => {
  return subscription.is_lifetime === true || 
         subscription.plan_interval === 'lifetime' || 
         subscription.payment_mode === 'one-time';
};

const MySubscription: React.FC = () => {
  const [subscription, setSubscription] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);
  
  const { t, language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // 检查用户是否已登录
  useEffect(() => {
    if (!authLoading) {
      setAuthChecked(true);
    }
  }, [authLoading]);

  useEffect(() => {
    if (authChecked && !authLoading && !user) {
      navigate('/login');
    }
  }, [authChecked, authLoading, user, navigate]);

  // 获取用户的订阅信息
  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await getBackendSubscription();
        console.log('Subscription data:', data);
        setSubscription(data.subscription ?? null);
      } catch (error) {
        console.error('Failed to fetch subscription data:', error);
        setError(t('subscriptionLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchSubscription();
    }
  }, [user]);

  // 如果认证检查完成且用户未登录，重定向到登录页
  // 处理查看定价页面
  const handleViewPlans = () => {
    navigate('/pricing');
  };
  
  // 处理查看客户门户
  const handleManageSubscription = async () => {
    if (!subscription || !subscription.customer_id) {
      setError(t('noCustomerId'));
      return;
    }
    
    try {
      setRedirectLoading(true);
      const data = await requestPortalSession({
        customer_id: subscription.customer_id,
        return_url: window.location.origin + '/mySubscription'
      });
      
      // 跳转到Stripe门户URL
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(t('noPortalUrl'));
      }
    } catch (error) {
      console.error('Failed to open subscription portal:', error);
      setError(t('portalOpenFailed'));
    } finally {
      setRedirectLoading(false);
    }
  };

  // 在已有条件下添加，识别 lifetime 订阅
  const isLifetime = subscription && isLifetimeSubscription(subscription);

  if (loading) {
    return (
      <Container>
        <Title>{t('loading')}</Title>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('error')}</NoSubscriptionTitle>
          <NoSubscriptionText>{error}</NoSubscriptionText>
        </NoSubscriptionMessage>
      </Container>
    );
  }
  
  if (!user) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('pleaseSignIn')}</NoSubscriptionTitle>
          <NoSubscriptionText>
            {t('signInToViewSubscription')}
          </NoSubscriptionText>
          <SubscribeButton onClick={() => navigate('/login')}>
            {t('signIn')}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>{t('noActiveSubscription')}</NoSubscriptionTitle>
          <NoSubscriptionText>
            {t('subscribeToUnlockFeatures')}
          </NoSubscriptionText>
          <SubscribeButton onClick={handleViewPlans}>
            {t('startFreeTrial')}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  // 判断是否在试用期内
  const isInTrialPeriod = subscription.is_trial && subscription.trial_end && 
    (new Date(subscription.trial_end * 1000) > new Date());

  // 判断是否是试用已过期但未订阅
  const isTrialExpired = subscription.is_trial && subscription.trial_end && 
    (new Date(subscription.trial_end * 1000) <= new Date()) && 
    subscription.status !== 'active';

  // 如果试用已过期且未订阅
  if (isTrialExpired) {
    return (
      <Container>
        <Title>{t('mySubscription')}</Title>
        <NoSubscriptionMessage>
          <NoSubscriptionTitle>
            {t('trialEnded')}
          </NoSubscriptionTitle>
          <NoSubscriptionText>
            {t('trialEndedDescription')}
          </NoSubscriptionText>
          <SubscribeButton onClick={handleViewPlans}>
            {t('upgradeToPro')}
          </SubscribeButton>
        </NoSubscriptionMessage>
      </Container>
    );
  }

  return (
    <Container>
      <LanguageToggle>
        <LanguageLabel>{t('language.label')}</LanguageLabel>
        {languageOptions.map(({ code, label }) => (
          <LanguageToggleButton
            key={code}
            type="button"
            $active={language === code}
            onClick={() => setLanguage(code)}
          >
            {label}
          </LanguageToggleButton>
        ))}
      </LanguageToggle>
      <Title>{t('mySubscription')}</Title>
      
      <UserInfoSection>
        <SectionTitle>{t('accountInformation')}</SectionTitle>
        
        <PeriodInfo>
          <IconWrapper>
            <FaUser />
          </IconWrapper>
          {t('name')}: {subscription.customer_name || user.displayName || t('unknown')}
        </PeriodInfo>
        
        <PeriodInfo>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          {t('email')}: {subscription.customer_email || user.email || t('unknown')}
        </PeriodInfo>
      </UserInfoSection>
      
      <SubscriptionCard>
        <PlanName>
          {subscription.plan_name || getPlanName(subscription.plan_id, subscription.plan_interval, language)}
          <StatusBadge status={subscription.status || subscription.subscription_status}>
            {getStatusText(subscription.status || subscription.subscription_status, language)}
          </StatusBadge>
        </PlanName>
        
        <PriceInfo>
          {subscription.plan_amount && (
            `${formatCurrency(subscription.plan_amount || subscription.amount_total, subscription.currency)} ${isLifetime ? `(${t('oneTimePayment')})` : `/ ${subscription.plan_interval === 'month' ? t('month') : t('year')}`}`
          )}
        </PriceInfo>
        
        {!isLifetime ? (
          <PeriodInfo>
            <IconWrapper>
              <FaCalendarAlt />
            </IconWrapper>
            {t('currentPeriod')}: {formatDate(subscription.current_period_start, language)} - {formatDate(subscription.current_period_end, language)}
          </PeriodInfo>
        ) : (
          <PeriodInfo>
            <IconWrapper>
              <FaCalendarAlt />
            </IconWrapper>
            {t('lifetimeAccess')}
          </PeriodInfo>
        )}
        
        {isInTrialPeriod && (
          <PeriodInfo>
            <IconWrapper>
              <FaClock />
            </IconWrapper>
            {t('trialDaysLeft', { days: getRemainingDays(subscription.trial_end) })}
          </PeriodInfo>
        )}
        
        {!isInTrialPeriod && !isLifetime && (
          <PeriodInfo>
            <IconWrapper>
              <FaCreditCard />
            </IconWrapper>
            {subscription.cancel_at_period_end
              ? t('willCancelAtPeriodEnd')
              : t('willAutoRenew')}
          </PeriodInfo>
        )}
        
        <PeriodInfo>
          <IconWrapper>
            <FaClock />
          </IconWrapper>
          {t('subscriptionStartedOn')}: {formatDate(subscription.created_at, language)}
        </PeriodInfo>
        
        {/* 购买日期显示 - 仅限 lifetime */}
        {isLifetime && subscription.payment_completed_at && (
          <PeriodInfo>
            <IconWrapper>
              <FaHistory />
            </IconWrapper>
            {t('purchaseDate')}: {formatDate(subscription.payment_completed_at, language)}
          </PeriodInfo>
        )}
        
        {/* 按钮区域 - 针对不同类型有不同显示 */}
        {(subscription.status === 'active' || subscription.subscription_status === 'active' || isInTrialPeriod) && (
          <ButtonGroup>
            {isInTrialPeriod ? (
              <ActionButton $primary onClick={handleViewPlans}>
                {t('upgradeToPro')}
              </ActionButton>
            ) : isLifetime ? (
              /* 终身版本不需要管理订阅按钮 */
              <ActionButton onClick={handleViewPlans}>
                {t('viewOtherPlans')}
              </ActionButton>
            ) : (
              <ActionButton $primary onClick={handleManageSubscription} disabled={redirectLoading}>
                {redirectLoading ? t('redirecting') : t('manageSubscription')}
              </ActionButton>
            )}
          </ButtonGroup>
        )}
      </SubscriptionCard>
    </Container>
  );
};

export default MySubscription; 
